import {
  type ChangeEvent,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FieldProps } from '@rjsf/utils';
import isEqual from 'lodash/isEqual';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { DropdownValueArrayCardUiOptions } from './schema';

/**
 * Row data type for the dropdown value array card
 */
type RowData = Record<string, string | undefined>;

/**
 * Property schema type for the dropdown value array card
 */
type PropertySchema = {
  title?: string;
  description?: string;
  enum?: string[];
  'ui:placeholder'?: string;
  'ui:widget'?: string;
};

/**
 * Field status type for the dropdown value array card
 */
type FieldStatus = 'default' | 'error';

/**
 * Required error message for the dropdown value array card
 */
const REQUIRED_ERROR_MESSAGE = 'Error. This is a required field.';

/**
 * Dropdown value array card component
 * @param props - The props for the component
 * @returns The dropdown value array card component
 */
export const DropdownValueArrayCard = ({
  onChange,
  formData,
  schema,
  idSchema,
  disabled,
  readonly,
  uiSchema,
}: FieldProps<RowData[]>) => {
  const theme = useTheme();
  const rowBackgroundColor =
    theme.palette.mode === 'dark' ? '#1A1A1A' : '#F4F4F4';
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const itemsSchema = useMemo(() => (schema as any)?.items ?? {}, [schema]);

  const propsSchema = useMemo(
    () => (itemsSchema.properties ?? {}) as Record<string, PropertySchema>,
    [itemsSchema],
  );

  const uiItems = useMemo(() => (uiSchema as any)?.items ?? {}, [uiSchema]);

  const uiOptions =
    ((uiSchema as any)?.['ui:options'] as DropdownValueArrayCardUiOptions) ??
    {};

  /**
   * Gets the visible keys for the dropdown value array card
   * @returns The visible keys for the dropdown value array card
   */
  const visibleKeys = useMemo(() => {
    const keys = Object.keys(propsSchema);
    return [
      ...['type', 'jsonFile'].filter(key => keys.includes(key)),
      ...keys.filter(key => key !== 'type' && key !== 'jsonFile'),
    ].slice(0, 2);
  }, [propsSchema]);

  /**
   * Gets the required keys for the dropdown value array card
   * @returns The required keys for the dropdown value array card
   */
  const requiredKeys = ((itemsSchema.required ?? []) as string[]).filter(key =>
    visibleKeys.includes(key),
  );

  const minItems = typeof schema.minItems === 'number' ? schema.minItems : 1;
  const maxItems =
    typeof schema.maxItems === 'number' ? schema.maxItems : undefined;

  /**
   * Creates an empty row with default values
   * @returns An empty row with default values
   */
  const createEmptyRow = useCallback(
    () =>
      visibleKeys.reduce((row, key) => {
        row[key] = '';
        return row;
      }, {} as RowData),
    [visibleKeys],
  );

  /**
   * Normalizes the rows for the dropdown value array card
   * @param rows The rows to normalize
   * @returns The normalized rows
   */
  const normalizeRows = useCallback(
    (rows?: RowData[]) => {
      if (Array.isArray(rows) && rows.length > 0) {
        return rows.map(row =>
          visibleKeys.reduce((normalized, key) => {
            normalized[key] = typeof row?.[key] === 'string' ? row[key] : '';
            return normalized;
          }, {} as RowData),
        );
      }

      return Array.from({ length: Math.max(minItems, 1) }, createEmptyRow);
    },
    [createEmptyRow, minItems, visibleKeys],
  );

  const [localValues, setLocalValues] = useState<RowData[]>(() =>
    normalizeRows(Array.isArray(formData) ? formData : undefined),
  );
  const [fieldStatus, setFieldStatus] = useState<Record<string, FieldStatus>>(
    {},
  );
  const [fieldHelpers, setFieldHelpers] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {},
  );
  const lastEmittedValueRef = useRef<RowData[] | undefined>(undefined);

  /**
   * Normalizes the form data and updates the local values
   */
  useEffect(() => {
    const normalized = normalizeRows(
      Array.isArray(formData) ? formData : undefined,
    );
    setLocalValues(previous =>
      isEqual(normalized, previous) ? previous : normalized,
    );
  }, [formData, normalizeRows]);

  /**
   * Emits the local values to the parent component
   */
  useEffect(() => {
    if (!isEqual(lastEmittedValueRef.current, localValues)) {
      lastEmittedValueRef.current = localValues;
      onChangeRef.current(localValues);
    }
  }, [localValues]);

  /**
   * Sets the field state for a specific row and key
   * @param rowIndex The row index
   * @param key The key of the field
   * @param status The status of the field
   * @param text The text of the field
   */
  const setFieldState = (
    rowIndex: number,
    key: string,
    status: FieldStatus,
    text: string,
  ) => {
    const fieldKey = `${rowIndex}:${key}`;
    setFieldStatus(previous => ({ ...previous, [fieldKey]: status }));
    setFieldHelpers(previous => ({ ...previous, [fieldKey]: text }));
  };

  /**
   * Validates a field for a specific row and key
   * @param rowIndex The row index
   * @param key The key of the field
   * @param value The value of the field
   */
  const validateField = (rowIndex: number, key: string, value: string) => {
    if (requiredKeys.includes(key) && value.trim().length === 0) {
      setFieldState(rowIndex, key, 'error', REQUIRED_ERROR_MESSAGE);
      return;
    }

    setFieldState(
      rowIndex,
      key,
      'default',
      propsSchema[key]?.description ?? '',
    );
  };

  /**
   * Handles the change of a field for a specific row and key
   * @param rowIndex The row index
   * @param key The key of the field
   * @returns A function that handles the change of the field
   */
  const handleChange =
    (rowIndex: number, key: string) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;
      setLocalValues(previous =>
        previous.map((row, index) =>
          index === rowIndex ? { ...row, [key]: value } : row,
        ),
      );

      const fieldKey = `${rowIndex}:${key}`;
      if (touchedFields[fieldKey] || fieldStatus[fieldKey] === 'error') {
        validateField(rowIndex, key, value);
      }
    };

  /**
   * Handles the blur of a field for a specific row and key
   * @param rowIndex The row index
   * @param key The key of the field
   */
  const handleBlur = (rowIndex: number, key: string) => {
    const fieldKey = `${rowIndex}:${key}`;
    setTouchedFields(previous => ({ ...previous, [fieldKey]: true }));
    validateField(rowIndex, key, localValues[rowIndex]?.[key] ?? '');
  };

  /**
   * Handles the removal of a row
   * @param rowIndex The row index
   */
  const handleRemove = (rowIndex: number) => {
    setLocalValues(previous =>
      previous.filter((_, index) => index !== rowIndex),
    );
    setFieldStatus({});
    setFieldHelpers({});
    setTouchedFields({});
  };

  const canAdd =
    !disabled &&
    !readonly &&
    (maxItems === undefined || localValues.length < maxItems);

  return (
    <Box>
      {schema.title && (
        <Typography variant="h6" sx={{ marginBottom: 1, marginTop: 0 }}>
          {schema.title}
        </Typography>
      )}
      {schema.description && (
        <Typography variant="body2" sx={{ marginBottom: 1 }}>
          {schema.description}
        </Typography>
      )}
      {(schema.title || schema.description) && (
        <Divider sx={{ marginBottom: 2, marginTop: 1 }} />
      )}

      <Stack spacing={2}>
        {localValues.map((row, rowIndex) => {
          const canRemove =
            !disabled &&
            !readonly &&
            localValues.length > Math.max(minItems, 1);

          return (
            <Fragment key={`${idSchema.$id}_row_${rowIndex}`}>
              <Box
                sx={{
                  backgroundColor: rowBackgroundColor,
                  borderRadius: 1,
                  padding: 2,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Stack spacing={2} sx={{ flex: 1 }}>
                    {visibleKeys.map(key => {
                      const property = propsSchema[key];
                      const enumValues = property?.enum;
                      const fieldKey = `${rowIndex}:${key}`;
                      const status = fieldStatus[fieldKey] ?? 'default';
                      const fieldUiSchema = uiItems?.[key] ?? {};
                      const placeholder =
                        fieldUiSchema['ui:placeholder'] ??
                        property?.['ui:placeholder'] ??
                        property?.title ??
                        key;
                      const isTextarea =
                        fieldUiSchema['ui:widget'] === 'textarea' ||
                        property?.['ui:widget'] === 'textarea' ||
                        key === 'jsonFile';

                      return (
                        <TextField
                          key={`${idSchema.$id}_${rowIndex}_${key}`}
                          id={`${idSchema.$id}_${rowIndex}_${key}`}
                          label={property?.title ?? key}
                          select={
                            Array.isArray(enumValues) && enumValues.length > 0
                          }
                          value={row[key] ?? ''}
                          onChange={handleChange(rowIndex, key)}
                          onBlur={() => handleBlur(rowIndex, key)}
                          placeholder={placeholder}
                          required={requiredKeys.includes(key)}
                          disabled={disabled || readonly}
                          multiline={isTextarea}
                          fullWidth
                          minRows={
                            key === 'jsonFile'
                              ? (uiOptions?.rows ?? 8)
                              : undefined
                          }
                          variant="outlined"
                          error={status === 'error'}
                          helperText={
                            fieldHelpers[fieldKey] ??
                            property?.description ??
                            ''
                          }
                          SelectProps={
                            Array.isArray(enumValues) && enumValues.length > 0
                              ? { displayEmpty: true }
                              : undefined
                          }
                          InputLabelProps={{ shrink: true }}
                        >
                          {Array.isArray(enumValues) &&
                            enumValues.length > 0 && [
                              <MenuItem
                                key={`${key}_placeholder`}
                                value=""
                                disabled
                              >
                                {placeholder}
                              </MenuItem>,
                              ...enumValues.map(enumValue => (
                                <MenuItem key={enumValue} value={enumValue}>
                                  {enumValue}
                                </MenuItem>
                              )),
                            ]}
                        </TextField>
                      );
                    })}
                  </Stack>
                  <IconButton
                    aria-label={`Remove configuration ${rowIndex + 1}`}
                    onClick={() => handleRemove(rowIndex)}
                    disabled={!canRemove}
                    size="medium"
                    sx={{ alignSelf: 'center' }}
                  >
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                </Stack>
              </Box>
            </Fragment>
          );
        })}
      </Stack>

      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          onClick={() =>
            setLocalValues(previous => [...previous, createEmptyRow()])
          }
          disabled={!canAdd}
          size="small"
          startIcon={<AddCircleOutlineIcon />}
        >
          {uiOptions.addButtonLabel ?? 'Add configuration'}
        </Button>
      </Box>
    </Box>
  );
};
